import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box, Divider, Grid } from "@mui/material";
import Button from "@mui/material/Button";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { FinancialIcon } from "components/Icons/Icons";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { ChatAssistantButton } from "mui-toolkit/ChatAssistantButton";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 } from "uuid";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { AllEquipmentPaymentsGrid } from "./AllEquipmentPaymentsGrid";
import EquipmentCard from "./EquipmentCard";
import { EquipmentDebtServiceStackedBarChart } from "./EquipmentDebtServiceStackedBarChart";
import EquipmentInventoryDataTable from "./EquipmentInventoryDataTable";

export const SYSTEM_PROMPT_EQUIPMENT_INVENTORY = "form_equipment_inventory";

export default function EquipmentInventoryWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const [workspaceMode, setWorkspaceMode] = useState(null);
  const CARD_GRID_KEY = "equipment_inventory_list";

  const { enqueueSnackbar } = useSnackbar();
  const { addAttachments } = useChatAssistantAttachments();
  const { actions: formStatusActions } = useFormStatus();
  const { component: confirmDialogComponent } = useDialog();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  // ---

  const [isLoading, setIsLoading] = useState(true);
  const [equipment, setEquipment] = useState([]);

  // State for filtered payments (shared between grid and chart)
  const [filteredPayments, setFilteredPayments] = useState([]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadEquipment = async () => {
    try {
      const equipmentFilter = {
        producer_token: globalState.producer_token,
        year: currentYear,
        is_active: true,
      };
      const equipment = await UsersApiHelper.selectEquipmentInventory(equipmentFilter, globalState);
      setEquipment(equipment);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setEquipment([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      loadEquipment();
    }
  }, [globalState?.producer_token, currentYear]);

  const handleAddNewClick = () => {
    navigate("/financial/equipment/0");
  };

  const handleEditClick = (equipment) => {
    if (equipment && equipment.id) {
      navigate(`/financial/equipment/${equipment.id}`);
    }
  };
  // --- --- ---

  useEffect(() => {
    addAttachments([{ name: "Equipment Inventory", content: equipment }]);
  }, [equipment]);

  // --- --- ---

  const handleChange = async (equipment, equipmentInventoryCountyPractices) => {
    if (!equipment.id) {
      equipment.id = v4();

      // Adding new county pratice items if creating a new equipment
      equipmentInventoryCountyPractices?.forEach((cp) => {
        cp.equipment_inventory_id = equipment.id;
      });

      UsersApiHelper.updateEquipmentInventoryCountyPractices(
        { EquipmentInventoryCountyPractices: equipmentInventoryCountyPractices },
        globalState
      ).catch((err) => {
        console.warn(err);
        enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
          preventDuplicate: true,
          variant: "error",
        });
      });
    }
    equipment.year = currentYear;

    UsersApiHelper.updateEquipmentInventory({ EquipmentInventory: [equipment] }, globalState)
      .then(() => {
        loadEquipment();
      })
      .catch((err) => {
        console.warn(err);
        formStatusActions.setIsFormDirty(true, false);
        enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
      });
  };

  const handleOnChangeRecords = async (records) => {
    records?.forEach((equipment) => {
      handleChange(equipment);
    });
  };
  // ---

  return (
    <>
      <WorkspaceHeader
        title="Equipment Inventory"
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
      />

      <WorkspacePageHeader
        title="Year"
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        additionalControls={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ChatAssistantButton
              systemPrompt={SYSTEM_PROMPT_EQUIPMENT_INVENTORY}
              attachments={[{ name: "Equipment", content: equipment }]}
              onChangeRecords={handleOnChangeRecords}
            />
          </Box>
        }
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {equipment
            ?.filter((item) => item.is_active !== false)
            ?.sort((a, b) => {
              const aName = a.name || "";
              const bName = b.name || "";
              return aName.localeCompare(bName);
            })
            ?.map((currentEquipment) => (
              <EquipmentCard
                key={currentEquipment.id}
                equipment={currentEquipment}
                onChange={handleChange}
                handleEditClick={handleEditClick}
              />
            ))}

          {!equipment?.length && (
            <EmptyCard
              text="No Inventory Defined"
              secondaryText="Get started by adding equipment"
              isLoading={isLoading}
            />
          )}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <EquipmentInventoryDataTable
          equipmentItems={equipment
            ?.filter((item) => item.is_active !== false)
            ?.sort((a, b) => {
              const aName = a.name || "";
              const bName = b.name || "";
              return aName.localeCompare(bName);
            })}
          onChange={handleChange}
          handleEditClick={handleEditClick}
          isLoading={isLoading}
        />
      )}

      <Divider />

      {/* Equipment Debt Service Chart - Total across all equipment loans */}
      <Grid container spacing={1} sx={{ mb: 3, mt: 3 }}>
        <Grid item xs={12} md={12}>
          <EquipmentDebtServiceStackedBarChart filteredPayments={filteredPayments} />
        </Grid>
      </Grid>

      {/* All Equipment Loan Payments Grid */}
      <AllEquipmentPaymentsGrid onFilteredDataChange={setFilteredPayments} />

      <WorkspaceActionBar
        content={
          <>
            <Box>
              <Button variant="contained" sx={{ width: "100%" }} onClick={handleAddNewClick}>
                <AddCircleOutline sx={{ mr: 1 }} />
                New Equipment
              </Button>
            </Box>
          </>
        }
      />

      {confirmDialogComponent}
    </>
  );
}
